// Weekly review hooks. GET /v1/reviews/current returns the generated row for
// the current IST week (or computes adherence + projection with an empty
// diagnosis when no row exists yet). Diagnosis generation runs on the Sunday
// 21:00 IST cron AND on-demand via POST /v1/reviews/run (one per IST week —
// ADR-0018 amendment). The accept/reject/dismiss endpoints act on whatever
// row was last persisted.
import type { GetCurrentReviewResponse } from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

const REVIEWS_KEY = ['reviews'] as const;

export function useCurrentReviewQuery() {
  return useQuery<GetCurrentReviewResponse>({
    queryKey: [...REVIEWS_KEY, 'current'],
    queryFn: () => api<GetCurrentReviewResponse>('/reviews/current'),
    // Review computation walks 7 days of NutritionLog + WeightLog + the
    // active plan. 60 s of staleness is fine — the user mostly opens this
    // screen once per visit, and mutations invalidate explicitly.
    staleTime: 60_000,
  });
}

/**
 * POST /v1/reviews/run — on-demand weekly diagnostic (ADR-0018 amendment).
 * Generates at most one review per IST week; returns the current review
 * either way. Invalidates the reviews cache so the screen re-renders with
 * the freshly persisted diagnosis.
 */
export function useRunReviewMutation() {
  const qc = useQueryClient();
  return useMutation<GetCurrentReviewResponse, Error, void>({
    mutationFn: () => api<GetCurrentReviewResponse>('/reviews/run', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
  });
}
